import { combineReducers } from 'redux';
import { push } from 'react-router-redux';
import { delay } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import accountApi from '../../../features/account-management/api';
import authApi from '../../auth/api';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
} from '../../../common/utils/reduxHelpers';
import { resetUploadedFilesAction, uploadFile } from '../../../features/files/ducks';
import { fetchLoansActions } from '../../../features/loans/ducks';
import { fetchMeActions } from '../../auth/ducks';

/**
 * ACTION TYPES
 */
export const FETCH_ACCOUNT_DOCUMENTS = 'documents/FETCH_ACCOUNT_DOCUMENTS';
export const FETCH_ACCOUNT_CONTRACTS = 'documents/FETCH_ACCOUNT_CONTRACTS';
export const UPDATE_ACCOUNT_DOCUMENTS = 'documents/UPDATE_ACCOUNT_DOCUMENTS';
export const SUBMIT_ACCOUNT_DOCUMENTS = 'documents/SUBMIT_ACCOUNT_DOCUMENTS';
export const UPLOAD_AND_SAVE_FILE = 'documents/UPLOAD_AND_SAVE_FILE';
export const SAVE_VERIFICATION_RESULT = 'documents/SAVE_VERIFICATION_RESULT';

/**
 * ACTIONS
 */
export const fetchAccountDocumentsActions = createApiActionCreators(FETCH_ACCOUNT_DOCUMENTS);
export const fetchAccountContractsActions = createApiActionCreators(FETCH_ACCOUNT_CONTRACTS);
export const updateAccountDocumentsActions = createApiActionCreators(UPDATE_ACCOUNT_DOCUMENTS);
export const submitAccountDocumentsActions = createApiActionCreators(SUBMIT_ACCOUNT_DOCUMENTS);
export const uploadFileAndUpdateAccountDocumentsActions = createActionCreator(UPLOAD_AND_SAVE_FILE);
export const saveVerificationResultAction = createApiActionCreators(SAVE_VERIFICATION_RESULT);

/**
 * REDUCERS
 */
const initialState = {
  documents: [],
  contracts: [],
  version: 0,
};

const documents = createReducer(initialState.documents, {
  [FETCH_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload.documents,
  },
  [UPDATE_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload.documents,
  },
});

const version = createReducer(initialState.version, {
  [FETCH_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload.version,
  },
  [UPDATE_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload.version,
  },
});

const contracts = createReducer(initialState.contracts, {
  [FETCH_ACCOUNT_CONTRACTS]: {
    [SUCCESS]: (state, payload) => payload,
  },
});

export default combineReducers({
  documents,
  version,
  contracts,
});

/**
 * SELECTORS
 */
export const selectDocumentsState = state => state.documents;
export const selectDocuments = state => selectDocumentsState(state).documents;
export const selectContracts = state => selectDocumentsState(state).contracts;
export const selectDocumentsVersion = state => selectDocumentsState(state).version;

/**
 * SAGAS
 */
export function* fetchAccountDocuments({ payload }) {
  const resp = yield call(accountApi.fetchAccountDocuments, payload);

  if (resp.ok) {
    yield put(fetchAccountDocumentsActions.success(resp.data));
  } else {
    yield put(fetchAccountDocumentsActions.failure(resp.error));
  }
}

export function* fetchAccountContracts({ payload }) {
  const resp = yield call(authApi.fetchAccountContracts, payload);

  if (resp.ok) {
    yield put(fetchAccountContractsActions.success(resp.data));
  } else {
    yield put(fetchAccountContractsActions.failure(resp.error));
  }
}

function* updateAccountDocuments({ payload }) {
  const resp = yield call(
    accountApi.updateAccountDocuments,
    payload.accountId,
    payload.documentsData
  );

  if (resp.ok) {
    yield put(updateAccountDocumentsActions.success(resp.data));

    for (let id of payload.uploadedFilesId) {
      yield put(resetUploadedFilesAction(id));
    }
  } else {
    yield put(updateAccountDocumentsActions.failure(resp.error));
  }
}

function* uploadFileAndUpdateAccountDocuments({ payload }) {
  const uploadFileResp = yield uploadFile({
    payload: payload.uploadFilePayload,
  });

  if (uploadFileResp.ok) {
    const newDocument = {
      file: {
        id: uploadFileResp.data.id,
        name: uploadFileResp.data.name,
      },
      type: payload.fileType,
    };

    const updateAccountDocumentsPayload = payload.updateAccountDocumentsPayload;

    updateAccountDocumentsPayload.documentsData.documents = [
      ...updateAccountDocumentsPayload.documentsData.documents,
      newDocument,
    ];

    yield updateAccountDocuments({
      payload: updateAccountDocumentsPayload,
    });
  }
}

function* submitAccountDocuments({ payload }) {
  const resp = yield call(accountApi.submitAccountDocuments, payload.accountId);

  if (resp.ok) {
    yield put(submitAccountDocumentsActions.success(resp.data));

    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    if (payload.redirect) {
      yield put(push(payload.redirect));
    }
  } else {
    yield put(submitAccountDocumentsActions.failure(resp.error));
  }
}

export const isVerificationFinished = account =>
  Boolean(account && account.verificationResult && account.verificationResult.result === 'FINISHED');

export function* saveVerificationResult({ payload }) {
  const { accountId, verificationResult } = payload;

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const response = yield call(accountApi.saveVerificationResult, accountId, verificationResult);

    if (!response.ok) {
      yield put(saveVerificationResultAction.failure(response.error));
      return;
    }

    if (isVerificationFinished(response.data)) {
      yield put(saveVerificationResultAction.success(response.data));
      yield put(fetchMeActions.success(response.data));

      yield call(fetchAccountDocuments, { payload: accountId });
      yield call(fetchAccountContracts, { payload: accountId });

      if (payload.redirect) {
        yield put(push(payload.redirect));
      }
      return;
    }

    if (attempt < 9) {
      yield delay(1000);
    }
  }

  yield put(saveVerificationResultAction.failure());
}

export function* documentsSaga() {
  yield takeLatest(createActionType(FETCH_ACCOUNT_DOCUMENTS, REQUEST), fetchAccountDocuments);
  yield takeLatest(createActionType(FETCH_ACCOUNT_CONTRACTS, REQUEST), fetchAccountContracts);
  yield takeLatest(createActionType(UPDATE_ACCOUNT_DOCUMENTS, REQUEST), updateAccountDocuments);
  yield takeLatest(createActionType(SUBMIT_ACCOUNT_DOCUMENTS, REQUEST), submitAccountDocuments);
  yield takeLatest(UPLOAD_AND_SAVE_FILE, uploadFileAndUpdateAccountDocuments);
  yield takeLatest(createActionType(SAVE_VERIFICATION_RESULT, REQUEST), saveVerificationResult);
}



// WEBPACK FOOTER //
// ./src/features/documents/ducks/index.js